import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Box, Chip, Tooltip, Typography } from '@mui/material';
import { StickyNote2Outlined as NoteIcon, CreditCardOff as CaptureIcon, Payments as OfflineIcon } from '@mui/icons-material';
import { orderStatusColor, orderStatusLabel, completeAwaitsCapture } from '../../../../utils/orderStatusLabel';
import { getOrderTypeDisplay } from '../orderDisplay';

export const offlineDue = (order) => order?.paymentStatus === 'OFFLINE' && !order?.offlinePaidAt && order?.status === 'CONFIRMED';

/**
 * One booking on the calendar. `compact` (Month, Week) shows `HH:mm Customer`; otherwise
 * `HH:mm Customer · Service [· N days]`. The time is the provider-local booked start (contract §10).
 * Colour follows the list's status colour; tentative proposals are dashed; cancellations struck through.
 */
export default function OrderEventChip({ event, note, compact = false, onClick }) {
  const { t } = useTranslation();
  const { order } = event;
  const customer = order.user?.name || t('orders.na');
  const span = event.multiDay ? ` · ${t('orders.calendar.days', { count: event.days.length })}` : '';
  const label = compact ? `${event.time} ${customer}` : `${event.time} ${customer} · ${getOrderTypeDisplay(order, t)}${span}`;
  const awaitingCapture = completeAwaitsCapture(order);
  const offline = offlineDue(order);

  const tooltip = (
    <Box>
      <Typography variant="caption" display="block">#{order.id} · {orderStatusLabel(order, t)}</Typography>
      <Typography variant="caption" display="block">
        {t('orders.calendar.bookedTime', { time: `${event.startDay} ${event.time}` })}{span}
      </Typography>
      {order.dogs?.length > 0 && (
        <Typography variant="caption" display="block">{t('orders.calendar.dogs')}: {order.dogs.map((d) => d.name).join(', ')}</Typography>
      )}
      {event.tentative && <Typography variant="caption" display="block">{t('orders.calendar.tentative')}</Typography>}
      {awaitingCapture && <Typography variant="caption" display="block">{t('orders.calendar.badgeAwaitingCapture')}</Typography>}
      {offline && <Typography variant="caption" display="block">{t('orders.calendar.badgeOfflineDue')}</Typography>}
      {note && <Typography variant="caption" display="block">📝 {note.latestBody.slice(0, 120)}</Typography>}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow>
      <Badge color="primary" variant="dot" invisible={!note} overlap="rectangular" sx={{ display: 'block', maxWidth: '100%' }}>
        <Chip
          size="small"
          clickable
          onClick={onClick}
          aria-label={`${label} ${t('orders.calendar.openOrder', { id: order.id })}`}
          label={label}
          color={event.cancelled ? 'default' : orderStatusColor(order)}
          variant={event.tentative ? 'outlined' : 'filled'}
          icon={offline ? <OfflineIcon /> : awaitingCapture ? <CaptureIcon /> : note ? <NoteIcon /> : undefined}
          sx={{
            width: '100%', justifyContent: 'flex-start', height: 22, fontSize: 12,
            opacity: event.cancelled ? 0.55 : 1,
            textDecoration: event.cancelled ? 'line-through' : 'none',
            borderStyle: event.tentative ? 'dashed' : 'solid',
            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
        />
      </Badge>
    </Tooltip>
  );
}
